import { collection, deleteDoc, doc, getDocs, limit, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Category, PaletteKey, Scope } from '@/types'
import { taskCollection, type Paths } from './tasks'

export function categoryCollection(p: Paths, scope: Scope) {
  return scope === 'shared' ? collection(db, 'households', p.hid, 'categories') : collection(db, 'users', p.uid, 'privateCategories')
}

export async function addCategory(p: Paths, scope: Scope, name: string, color: PaletteKey, order: number): Promise<string> {
  const ref = doc(categoryCollection(p, scope))
  await setDoc(ref, {
    name: name.trim().slice(0, 60),
    color,
    order,
    archived: false,
    createdBy: p.uid,
    updatedBy: p.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateCategory(p: Paths, c: Category, patch: Partial<Pick<Category, 'name' | 'color' | 'order' | 'archived'>>): Promise<void> {
  await updateDoc(doc(categoryCollection(p, c.scope), c.id), { ...patch, updatedBy: p.uid, updatedAt: serverTimestamp() })
}

/** Rewrites `order` for a whole list after a drag. */
export async function reorderCategories(p: Paths, scope: Scope, ids: string[]): Promise<void> {
  const batch = writeBatch(db)
  ids.forEach((id, i) => {
    batch.update(doc(categoryCollection(p, scope), id), { order: i + 1, updatedBy: p.uid, updatedAt: serverTimestamp() })
  })
  await batch.commit()
}

export async function deleteCategory(p: Paths, c: Category): Promise<void> {
  await deleteDoc(doc(categoryCollection(p, c.scope), c.id))
}

/**
 * Whether any task this person can see still uses a category, done ones of any
 * age included: history is kept for good, so the loaded tasks are not enough.
 */
export async function categoryInUse(p: Paths, id: string): Promise<boolean> {
  for (const scope of ['shared', 'private'] as const) {
    const snap = await getDocs(query(taskCollection(p, scope), where('categoryId', '==', id), limit(1)))
    if (!snap.empty) return true
  }
  return false
}
